import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { NJButton, NJFormItem, NJSelect, NJText } from '@engie-group/fluid-design-system-react';
import { fetchComments, postComment, deleteComment, editComment, type Comment } from '../bffComments';
import { useCurrentUser } from './useCurrentUser';
import { useCommentIdentity } from './useCommentIdentity';
import { ReplyForm } from './CommentsOverlay';
import { iconBtnStyle, formatCommentDate } from './styles';
import { ThreadActions } from './ThreadActions';
import { submitOnEnter } from './submitOnEnter';

const LAYOUT_HEADER_HEIGHT = 48;

// ── Avatar ────────────────────────────────────────────────────────────────────

const PALETTE = ['#4f6ef7','#e05c97','#2eaa7f','#f0883e','#9b59d0','#1aadce','#d94f3d','#6ab04c'];

function avatarBg(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

function Avatar({ name }: { name: string }) {
  return (
    <div style={{
      width: 28, height: 28, borderRadius: '50%', background: avatarBg(name),
      color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center', userSelect: 'none',
    }}>
      {initials(name)}
    </div>
  );
}

// ── CommentItem ───────────────────────────────────────────────────────────────

interface CommentItemProps {
  comment: Comment;
  replies?: Comment[];
  slug: string;
  currentUserEmail: string | undefined;
  isAdmin?: boolean;
  isPinned?: boolean;
  isReply?: boolean;
  isHighlighted?: boolean;
  onPinClick?: (commentId: string) => void;
  onMovePin?: (commentId: string) => void;
}

function CommentItem({
  comment, replies = [], slug, currentUserEmail, isAdmin = false,
  isPinned = false, isReply = false, isHighlighted = false, onPinClick, onMovePin,
}: CommentItemProps) {
  const isOwn = isAdmin || (!!currentUserEmail && currentUserEmail === comment.authorEmail);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(comment.text);
  const [error, setError] = useState<string | null>(null);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const textareaRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (editMode) textareaRef.current?.querySelector('textarea')?.focus();
  }, [editMode]);

  const { mutate: doDelete, isPending: isDeleting } = useMutation({
    mutationFn: () => deleteComment(slug, comment.commentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['comments', slug] }),
    onError: () => setError('Impossible de supprimer.'),
  });

  const { mutate: doEdit, isPending: isEditing } = useMutation({
    mutationFn: () => editComment(slug, comment.commentId, draft),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['comments', slug] });
      setEditMode(false);
    },
    onError: () => setError('Impossible de sauvegarder.'),
  });

  const displayName = comment.authorName || comment.authorEmail;

  return (
    <div
      style={{
        display: 'flex', gap: 10,
        padding: isReply ? '6px 0' : '10px 0',
        borderBottom: isReply ? 'none' : '1px solid var(--nj-core-color-grey-100)',
        background: isHighlighted ? 'var(--nj-semantic-color-background-accent-secondary-default)' : 'transparent',
        borderRadius: isHighlighted ? 6 : 0,
        transition: 'background 0.2s',
        ...(isReply ? { marginLeft: 38, paddingLeft: 10, borderLeft: '2px solid var(--nj-core-color-grey-200)' } : {}),
      }}
    >
      {!isReply && <Avatar name={displayName} />}

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
            {isPinned && (
              <button onClick={() => onPinClick?.(comment.commentId)} title="Voir sur la page" style={{ ...iconBtnStyle, padding: 0 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 13, color: 'var(--nj-semantic-color-text-accent-default)' }}>edit_note</span>
              </button>
            )}
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--nj-semantic-color-text-neutral-primary-default)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayName}
            </span>
            <span style={{ fontSize: 11, color: 'var(--nj-core-color-grey-500)', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {formatCommentDate(comment.createdAt)}{comment.updatedAt ? ' · modifié' : ''}
            </span>
          </div>
          {!editMode && (
            <div style={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              {isOwn && <button onClick={() => setEditMode(true)} title="Modifier" style={{ ...iconBtnStyle, width: 24, height: 24 }} disabled={isDeleting}>
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>edit</span>
              </button>}
              <button onClick={() => doDelete()} title="Supprimer" disabled={isDeleting} style={{ ...iconBtnStyle, width: 24, height: 24 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>delete</span>
              </button>
            </div>
          )}
        </div>

        {error && <span style={{ fontSize: 11, color: 'var(--nj-semantic-color-text-danger-default)' }}>{error}</span>}
        {!isReply && <ThreadActions comment={comment} slug={slug} onMove={isPinned ? () => onMovePin?.(comment.commentId) : undefined} />}
        {isPinned && !comment.anchor && <NJText scale="xs">Ancienne annotation : repositionne-la pour l’ancrer à un composant.</NJText>}

        {editMode ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
            <div ref={textareaRef} style={{ width: '100%' }}>
              <NJFormItem
                id={`edit-${comment.commentId}`} label="" isMultiline rows={3}
                value={draft}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => { setDraft(e.target.value); setError(null); }}
                onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); if (draft.trim()) doEdit(); }
                }}
                placeholder="Ctrl+Entrée pour sauvegarder"
              />
            </div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <NJButton label="Annuler" variant="secondary" scale="sm" onClick={() => { setDraft(comment.text); setEditMode(false); }} />
              <NJButton label={isEditing ? 'Sauvegarde…' : 'Sauvegarder'} variant="primary" scale="sm" onClick={() => doEdit()} disabled={!draft.trim() || isEditing} />
            </div>
          </div>
        ) : (
          <>
            <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--nj-semantic-color-text-neutral-primary-default)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {comment.text}
            </span>
            {replies.map((r) => (
              <CommentItem key={r.commentId} comment={r} slug={slug} currentUserEmail={currentUserEmail} isAdmin={isAdmin} isReply />
            ))}
            {!isReply && !showReplyForm && (
              <button
                onClick={() => setShowReplyForm(true)}
                style={{ ...iconBtnStyle, gap: 3, fontSize: 11, marginTop: 2, alignSelf: 'flex-start', color: 'var(--nj-core-color-grey-500)' }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 13 }}>reply</span>
                Répondre
              </button>
            )}
            {!isReply && showReplyForm && (
              <ReplyForm slug={slug} parentCommentId={comment.commentId} onDone={() => setShowReplyForm(false)} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── SectionHeader ─────────────────────────────────────────────────────────────

function SectionHeader({ icon, label, count }: { icon: string; label: string; count: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '12px 0 6px' }}>
      <span className="material-symbols-outlined" style={{ fontSize: 14, color: 'var(--nj-core-color-grey-500)' }}>{icon}</span>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--nj-core-color-grey-500)' }}>
        {label}
      </span>
      {count > 0 && (
        <span style={{ fontSize: 10, fontWeight: 700, background: 'var(--nj-core-color-grey-200)', color: 'var(--nj-core-color-grey-700)', borderRadius: 10, padding: '1px 6px' }}>
          {count}
        </span>
      )}
      <div style={{ flex: 1, height: 1, background: 'var(--nj-core-color-grey-100)' }} />
    </div>
  );
}

// ── AddCommentForm ────────────────────────────────────────────────────────────

function AddCommentForm({ slug, onSuccess }: { slug: string; onSuccess: () => void }) {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { name: authorName } = useCurrentUser();

  const { mutate, isPending } = useMutation({
    mutationFn: () => postComment(slug, text, { authorName: authorName ?? undefined }),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['comments', slug] });
      setText('');
      onSuccess();
    },
    onError: () => setError("Échec de l'envoi. Vérifie ta connexion et réessaie."),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
      <NJFormItem
        id="new-comment-text" label="" isMultiline rows={3}
        value={text}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => { setText(e.target.value); setError(null); }}
        onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
          submitOnEnter(e, () => {
            if (text.trim() && text.length <= 2000 && !isPending) mutate();
          });
        }}
        placeholder="Ajouter un commentaire… (Entrée pour envoyer, Maj+Entrée pour une nouvelle ligne)"
        subscriptMessage={text.length > 1800 ? `${text.length}/2000 caractères` : undefined}
      />
      {error && <span style={{ fontSize: 11, color: 'var(--nj-semantic-color-text-danger-default)' }}>{error}</span>}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <NJButton
          label={isPending ? 'Envoi…' : 'Envoyer'} variant="primary" scale="sm"
          onClick={() => { if (text.trim() && text.length <= 2000 && !isPending) mutate(); }}
          disabled={text.trim().length === 0 || text.length > 2000 || isPending}
        />
      </div>
    </div>
  );
}

// ── CommentsPanel — pure CSS drawer, no FDS modal ─────────────────────────────

export const COMMENTS_PANEL_ID = 'comments-panel';

interface CommentsPanelProps {
  slug: string;
  onHighlightPin?: (commentId: string | null) => void;
  highlightCommentId?: string | null;
  onClose: () => void;
  onMovePin: (commentId: string) => void;
}

export function CommentsPanel({ slug, onHighlightPin, highlightCommentId, onClose, onMovePin }: CommentsPanelProps) {
  const { currentUserEmail, isAdmin } = useCommentIdentity();
  const listEndRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'open' | 'resolved' | 'all'>('open');

  const { data: comments = [], isLoading, isError } = useQuery({
    queryKey: ['comments', slug],
    queryFn: () => fetchComments(slug),
    enabled: !!slug,
    staleTime: 30_000,
  });

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const filtered = comments.filter((c) => status === 'all' || (status === 'resolved' ? !!c.resolved : !c.resolved));
  const rootPins = filtered.filter((c) => c.xPct !== undefined && !c.parentCommentId);
  const general  = filtered.filter((c) => c.xPct === undefined  && !c.parentCommentId);
  const repliesOf = (id: string) => comments.filter((c) => c.parentCommentId === id);

  const total = rootPins.length + general.length;

  const scrollToBottom = () =>
    setTimeout(() => listEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, top: LAYOUT_HEADER_HEIGHT,
          background: 'rgba(0,0,0,0.18)', zIndex: 999,
        }}
      />

      {/* Drawer */}
      <div
        id={COMMENTS_PANEL_ID}
        style={{
          position: 'fixed',
          top: LAYOUT_HEADER_HEIGHT, right: 0, bottom: 0,
          width: 380, maxWidth: '90vw',
          background: '#fff',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.10)',
          zIndex: 1000,
          display: 'flex', flexDirection: 'column',
          fontFamily: 'var(--nj-core-font-family-sans)',
        }}
      >
        {/* ── Header ── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 16px', height: 52, flexShrink: 0,
          borderBottom: '1px solid var(--nj-core-color-grey-100)',
        }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--nj-semantic-color-text-neutral-primary-default)' }}>
            Commentaires{total > 0 ? ` (${total})` : ''}
          </span>
          <button
            onClick={onClose}
            title="Fermer (Échap)"
            style={{
              ...iconBtnStyle,
              width: 32, height: 32, borderRadius: 6,
              color: 'var(--nj-core-color-grey-600)',
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
          </button>
        </div>
        <div style={{ padding: 'var(--nj-semantic-size-spacing-16)' }}>
          <NJSelect.Root id="comments-status" label="Afficher les discussions" initialValue="open"
            listNavigationLabel="Utilise les flèches puis Entrée pour choisir."
            buttonDefaultValueLabel="Choisir un statut"
            onChange={(value) => {
              if (value === 'open' || value === 'resolved' || value === 'all') setStatus(value);
            }}>
            <NJSelect.Item value="open">Ouvertes</NJSelect.Item>
            <NJSelect.Item value="resolved">Résolues</NJSelect.Item>
            <NJSelect.Item value="all">Toutes</NJSelect.Item>
          </NJSelect.Root>
        </div>

        {/* ── Scrollable list ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px', minHeight: 0 }}>
          {isLoading && (
            <span style={{ display: 'block', padding: '20px 0', fontSize: 13, color: 'var(--nj-core-color-grey-500)' }}>
              Chargement…
            </span>
          )}
          {isError && (
            <span style={{ display: 'block', padding: '20px 0', fontSize: 13, color: 'var(--nj-semantic-color-text-danger-default)' }}>
              Impossible de charger les commentaires.
            </span>
          )}
          {!isLoading && !isError && (
            <>
              <SectionHeader icon="edit_note" label="Annotations" count={rootPins.length} />
              {rootPins.length === 0 ? (
                <span style={{ display: 'block', fontSize: 12, color: 'var(--nj-core-color-grey-500)', padding: '4px 0 8px' }}>
                  Aucune annotation — utilise <strong>edit_note</strong> dans le header.
                </span>
              ) : rootPins.map((c) => (
                <CommentItem
                  key={c.commentId} comment={c} replies={repliesOf(c.commentId)}
                  slug={slug} currentUserEmail={currentUserEmail} isAdmin={isAdmin} isPinned
                  onMovePin={onMovePin}
                  isHighlighted={highlightCommentId === c.commentId}
                  onPinClick={(id) => onHighlightPin?.(highlightCommentId === id ? null : id)}
                />
              ))}

              <SectionHeader icon="forum" label="Commentaires généraux" count={general.length} />
              {general.length === 0 && (
                <span style={{ display: 'block', fontSize: 12, color: 'var(--nj-core-color-grey-500)', padding: '4px 0 8px' }}>
                  Aucun commentaire général.
                </span>
              )}
              {general.map((c) => (
                <CommentItem
                  key={c.commentId} comment={c} replies={repliesOf(c.commentId)}
                  slug={slug} currentUserEmail={currentUserEmail} isAdmin={isAdmin}
                />
              ))}
              <div ref={listEndRef} style={{ height: 8 }} />
            </>
          )}
        </div>

        {/* ── Footer — new comment form ── */}
        <div style={{
          flexShrink: 0,
          padding: '12px 16px 16px',
          borderTop: '1px solid var(--nj-core-color-grey-100)',
          background: '#fff',
        }}>
          <AddCommentForm slug={slug} onSuccess={scrollToBottom} />
        </div>
      </div>
    </>
  );
}
